<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Preferences</h1>
				<p class="text-muted mb-0">
					Your own settings. These are yours alone — nothing here changes what
					another member of the district sees, and the district's own
					configuration lives under Configuration.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- Tag appearance: the member's own symbols for the district's tags and
		     subject kinds (doc/in-progress/site-tagging-and-symbology.md §5.2).
		     The same component mounts in the Map Review rail so the legend and the
		     settings agree; the server resolved every row, so this page never
		     derives the precedence order itself. -->
		<div class="card settings-card mb-4">
			<div class="card-header bg-light">
				<h5><i class="bi bi-palette me-2"></i> Tag Appearance</h5>
			</div>
			<div class="card-body">
				<p class="text-muted">
					How the district's tags are drawn for you on the map. A tag's style
					here replaces the district's for your view only; the district's tag
					vocabulary, ranking and filtering are unchanged. District tag styling
					outranks your kind defaults — to change one tag, override that tag.
				</p>
				<div v-if="loading" class="text-muted">
					<span
						class="spinner-border spinner-border-sm me-2"
						role="status"
						aria-hidden="true"
					></span>
					Loading your symbology…
				</div>
				<SymbologyPane
					v-else-if="symbologyCatalog"
					:catalog="symbologyCatalog"
					@refresh="refresh"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
// The member-facing preferences section. It begins with tag appearance because
// that is the first user-scoped setting the product has; the page exists so
// those settings have one home, rather than each living in the surface that
// happens to draw them.
import { onMounted, ref } from "vue";

import SymbologyPane from "@/components/map-review/SymbologyPane.vue";
import { loadSymbologyCatalog, symbologyCatalog } from "@/lib/symbologyCatalog";

const error = ref("");
const loading = ref(true);

/** A forced read after a write: an override changes what the server resolves,
 * so the held catalog is stale until it is re-read. */
function refresh(): void {
	void loadSymbologyCatalog(true).catch(() => {});
}

onMounted(async () => {
	try {
		await loadSymbologyCatalog();
	} catch {
		error.value = "Failed to load your symbology settings.";
	} finally {
		loading.value = false;
	}
});
</script>
